import { useEffect, useState } from "react";
import api from "../lib/api";

const DEFAULT_LOGO = "https://customer-assets.emergentagent.com/job_morarbem-saas/artifacts/rgf0wg1w_logo%20morar%20bem.png";
const DEFAULT_PWA = "/icon-512.svg";

let cached = null;
let inflight = null;

/**
 * Hook que retorna a identidade visual (logo + favicon + PWA icon) carregada de /settings/public.
 * Cacheia em memória para evitar múltiplas chamadas. Aplica dinamicamente:
 * - favicon (<link rel="icon">)
 * - apple-touch-icon (<link rel="apple-touch-icon">)
 * - manifest PWA via data: URL (<link rel="manifest">) — controla o ícone do "Instalar App"
 */
export default function useBrandIdentity() {
  const [brand, setBrand] = useState(cached || { logo: DEFAULT_LOGO, favicon: null, pwaIcon: DEFAULT_PWA, shortName: "MorarBem", fullName: "Morar Bem · Guia + Cashback" });

  useEffect(() => {
    if (cached) return;
    if (!inflight) {
      inflight = api.get("/settings/public").then((r) => {
        const data = r.data || {};
        cached = {
          logo: (data.brand_logo_url && data.brand_logo_url.trim()) || DEFAULT_LOGO,
          favicon: (data.brand_favicon_url && data.brand_favicon_url.trim()) || null,
          pwaIcon: (data.brand_pwa_icon_url && data.brand_pwa_icon_url.trim()) || DEFAULT_PWA,
          shortName: (data.brand_short_name && data.brand_short_name.trim()) || "MorarBem",
          fullName: (data.brand_full_name && data.brand_full_name.trim()) || "Morar Bem · Guia + Cashback",
        };
        return cached;
      }).catch(() => ({ logo: DEFAULT_LOGO, favicon: null, pwaIcon: DEFAULT_PWA, shortName: "MorarBem", fullName: "Morar Bem · Guia + Cashback" }));
    }
    inflight.then((c) => {
      setBrand(c);
      const setLink = (rel, href) => {
        if (!href) return;
        let link = document.querySelector(`link[rel='${rel}']`);
        if (!link) {
          link = document.createElement("link");
          link.rel = rel;
          document.head.appendChild(link);
        }
        link.href = href;
      };
      // 1) Favicon
      if (c.favicon) setLink("icon", c.favicon);
      // 2) Apple touch icon (iOS "Add to Home Screen")
      if (c.pwaIcon) setLink("apple-touch-icon", c.pwaIcon);
      // 3) Manifest dinâmico via data: URL — controla o ícone do "Instalar App"
      const isSvg = c.pwaIcon.toLowerCase().endsWith(".svg");
      const manifest = {
        short_name: c.shortName,
        name: c.fullName,
        description: "Guia comercial multicidades com cashback global (BemCash)",
        icons: [{
          src: c.pwaIcon,
          sizes: isSvg ? "192x192 512x512" : "512x512",
          type: isSvg ? "image/svg+xml" : "image/png",
          purpose: "any maskable",
        }],
        start_url: "/",
        scope: "/",
        display: "standalone",
        orientation: "portrait-primary",
        theme_color: "#1e3a8a",
        background_color: "#fafaf9",
        categories: ["shopping", "lifestyle", "business"],
        lang: "pt-BR",
      };
      try {
        const dataUrl = "data:application/manifest+json;base64," + btoa(unescape(encodeURIComponent(JSON.stringify(manifest))));
        setLink("manifest", dataUrl);
      } catch (_) { /* ignore */ }
      // 4) Atualiza document.title
      if (c.fullName) document.title = c.fullName;
    });
  }, []);

  return brand;
}
